import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { algoService, AuditEvent, AuditRunSummary } from '../../services/algoService';
import '../Dashboard.css';

const STATUS_COLORS: Record<string, string> = {
  success: '#16a34a',
  failure: '#dc2626',
  warning: '#d97706',
  skip:    '#6b7280',
};

const CATEGORY_LABELS: Record<string, string> = {
  system:            'System',
  trade_lifecycle:   'Trade Lifecycle',
  signal_generation: 'Pick Generation',
  agent_workflow:    'Agent Workflow',
};

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Group events by category and symbol
function groupEvents(events: AuditEvent[]): { category: string; symbol: string | null; events: AuditEvent[] }[] {
  const groups: { category: string; symbol: string | null; events: AuditEvent[] }[] = [];
  let currentGroup: { category: string; symbol: string | null; events: AuditEvent[] } | null = null;

  for (const e of events) {
    const key = e.category === 'signal_generation' || e.category === 'agent_workflow'
      ? `${e.category}#${e.symbol || ''}`
      : e.category;

    if (!currentGroup || `${currentGroup.category}#${currentGroup.symbol || ''}` !== key) {
      currentGroup = { category: e.category, symbol: e.symbol, events: [] };
      groups.push(currentGroup);
    }
    currentGroup.events.push(e);
  }

  return groups;
}

const RunCard: React.FC<{ run: AuditRunSummary; selected: boolean; onClick: () => void }> = ({ run, selected, onClick }) => {
  const statusColor = run.errors > 0 ? '#dc2626' : '#16a34a';
  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? '#1e293b' : '#111827',
        border: selected ? '1px solid #3b82f6' : '1px solid #1f2937',
        borderRadius: 8,
        padding: '12px 14px',
        cursor: 'pointer',
        marginBottom: 8,
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600 }}>
          {run.startedAt ? fmtDate(run.startedAt) : 'Unknown'}
        </span>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#9ca3af' }}>
        <span>{run.symbolsProcessed.length} sym</span>
        <span>{run.signalsCreated} pick{run.signalsCreated !== 1 ? 's' : ''}</span>
        {run.tradesExecuted > 0 && <span style={{ color: '#16a34a' }}>{run.tradesExecuted} trade{run.tradesExecuted !== 1 ? 's' : ''}</span>}
        {run.errors > 0 && <span style={{ color: '#dc2626' }}>{run.errors} err</span>}
      </div>
    </div>
  );
};

const EventDot: React.FC<{ status: string }> = ({ status }) => (
  <span style={{
    width: 10, height: 10, borderRadius: '50%',
    background: STATUS_COLORS[status] || '#6b7280',
    display: 'inline-block', flexShrink: 0,
  }} />
);

const TimelineEvent: React.FC<{ event: AuditEvent; onClick: () => void; expanded: boolean }> = ({ event, onClick, expanded }) => {
  const typeLabel = event.eventType.replace(/_/g, ' ');
  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
          cursor: 'pointer', borderRadius: 6,
          background: expanded ? '#1e293b' : 'transparent',
          transition: 'background 0.15s',
        }}
      >
        <EventDot status={event.status} />
        <span style={{ color: '#e5e7eb', fontSize: 13, flex: 1, textTransform: 'capitalize' }}>
          {typeLabel}
          {event.summary && <span style={{ color: '#9ca3af', marginLeft: 8, fontWeight: 400 }}>{event.summary}</span>}
        </span>
        {event.durationMs !== null && (
          <span style={{ color: '#6b7280', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {fmtDuration(event.durationMs)}
          </span>
        )}
      </div>
      {expanded && event.detail && (
        <div style={{
          margin: '4px 0 8px 20px', padding: '10px 14px',
          background: '#0f172a', borderRadius: 6, border: '1px solid #1f2937',
        }}>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
            {event.timestamp && <span>Time: {fmtTime(event.timestamp)}</span>}
            {event.symbol && <span style={{ marginLeft: 12 }}>Symbol: {event.symbol}</span>}
            {event.policyId && <span style={{ marginLeft: 12 }}>Policy: {event.policyId}</span>}
          </div>
          <pre style={{
            color: '#d1d5db', fontSize: 12, margin: 0,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace',
          }}>
            {JSON.stringify(event.detail, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export const AlgoAudit: React.FC = () => {
  const { token } = useAuth();
  const [runs, setRuns] = useState<AuditRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runEvents, setRunEvents] = useState<AuditEvent[]>([]);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [engineFilter, setEngineFilter] = useState('');

  const fetchRuns = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const opts: { engineId?: string; limit?: number } = { limit: 200 };
      if (engineFilter) opts.engineId = engineFilter;
      const data = await algoService.getAuditRuns(token, opts);
      setRuns(data.runs || []);
      if (data.runs?.length > 0 && !selectedRunId) {
        setSelectedRunId(data.runs[0].runId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [token, engineFilter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  useEffect(() => {
    if (!token || !selectedRunId) return;
    setLoadingEvents(true);
    algoService.getAuditRunEvents(token, selectedRunId)
      .then(data => setRunEvents(data.events || []))
      .catch(() => setRunEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [token, selectedRunId]);

  // Stats
  const today = new Date().toISOString().split('T')[0];
  const todayRuns = runs.filter(r => r.startedAt?.startsWith(today));
  const todaySignals = todayRuns.reduce((s, r) => s + r.signalsCreated, 0);
  const todayTrades = todayRuns.reduce((s, r) => s + r.tradesExecuted, 0);
  const todayErrors = todayRuns.reduce((s, r) => s + r.errors, 0);

  const eventGroups = groupEvents(runEvents);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ color: '#e5e7eb', margin: 0, fontSize: 22 }}>Audit Log</h1>
          <button
            onClick={fetchRuns}
            style={{
              background: '#1f2937', color: '#9ca3af', border: '1px solid #374151',
              borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13,
            }}
          >
            Refresh
          </button>
        </div>

        {/* Stats bar */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Runs Today', value: todayRuns.length, color: '#3b82f6' },
            { label: 'Picks', value: todaySignals, color: '#16a34a' },
            { label: 'Trades', value: todayTrades, color: '#d97706' },
            { label: 'Errors', value: todayErrors, color: todayErrors > 0 ? '#dc2626' : '#6b7280' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#111827', borderRadius: 8, padding: '12px 20px',
              border: '1px solid #1f2937', flex: 1, textAlign: 'center',
            }}>
              <div style={{ color: s.color, fontSize: 24, fontWeight: 700 }}>{s.value}</div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Two-panel layout */}
        <div style={{ display: 'flex', gap: 16, minHeight: 500 }}>
          {/* Left: Run list */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <div style={{ marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Filter by engine ID..."
                value={engineFilter}
                onChange={e => setEngineFilter(e.target.value)}
                style={{
                  width: '100%', background: '#111827', color: '#e5e7eb',
                  border: '1px solid #374151', borderRadius: 6, padding: '8px 12px',
                  fontSize: 13, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {loading ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>Loading runs...</div>
            ) : runs.length === 0 ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>No audit runs found</div>
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
                {runs.map(run => (
                  <RunCard
                    key={run.runId}
                    run={run}
                    selected={selectedRunId === run.runId}
                    onClick={() => { setSelectedRunId(run.runId); setExpandedEvent(null); }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: Step timeline */}
          <div style={{ flex: 1, background: '#111827', borderRadius: 8, border: '1px solid #1f2937', padding: '16px 20px', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            {!selectedRunId ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 60 }}>Select a run to view its timeline</div>
            ) : loadingEvents ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 60 }}>Loading events...</div>
            ) : runEvents.length === 0 ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 60 }}>No events for this run</div>
            ) : (
              <>
                {eventGroups.map((group, gi) => (
                  <div key={gi} style={{ marginBottom: 8 }}>
                    {/* Category divider */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 0', marginBottom: 4,
                    }}>
                      <div style={{ height: 1, flex: 1, background: '#1f2937' }} />
                      <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {CATEGORY_LABELS[group.category] || group.category}
                        {group.symbol && ` (${group.symbol})`}
                      </span>
                      <div style={{ height: 1, flex: 1, background: '#1f2937' }} />
                    </div>

                    {/* Timeline connector */}
                    <div style={{ borderLeft: '2px solid #1f2937', marginLeft: 14, paddingLeft: 6 }}>
                      {group.events.map(event => (
                        <TimelineEvent
                          key={event.eventId}
                          event={event}
                          expanded={expandedEvent === event.eventId}
                          onClick={() => setExpandedEvent(expandedEvent === event.eventId ? null : event.eventId)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
